/**
 * MANAGE MEMBERS - the Firestore half (task #31, Bobbie 2026-08-30:
 * "Directors + managers", placed "right there at the top of the group",
 * then widened the same night to every non-class board - Jr Staff board,
 * comp board, parent board). The pure composition helpers live in
 * lib/boardMembershipOverlay.ts (Firestore-free, re-exported below); this
 * file is only the reads and writes.
 *
 * ONE overlay doc per board: {addedUids, removedUids}. The RULE is the
 * authority on who this actually grants or takes (shared/firestore.rules'
 * isCompBoardMember() / isJrStaffBoardMember() / isParentBoardMember()) -
 * effective membership = (derived roll OR added) AND NOT removed, and a
 * director/manager REMOVE is sticky against the hourly auto-roll sweep
 * because that sweep tx.sets a DIFFERENT doc (compBoardMembership/current,
 * jrStaffBoardMembership/current) and never touches this one. Everything
 * in this file is a client convenience on top of that rule, never a second
 * copy of it - nothing here computes "am I really a member" for access
 * purposes; only for DISPLAY in the Manage members sheet.
 *
 * WIDENED PAST THE BOARDS 2026-09-01 (Bobbie: "add a button at the top that
 * directors can add specific people to the command center similar to the
 * boards"). Every function below now takes a MembershipOverlayKey rather
 * than a BoardKey, so the Command Center's own overlay doc
 * (commandCenterMembershipOverlay/current) rides these exact reads and
 * writes instead of a second hand-rolled copy. Its own gate is
 * DIRECTOR-ONLY in the rules, not director-or-manager - see
 * lib/commandCenterMembers.ts and the commandCenterMembershipOverlay match
 * block in shared/firestore.rules.
 */

import { useEffect, useRef, useState } from 'react';
import { arrayRemove, arrayUnion, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import {
  OVERLAY_COLLECTION,
  ROLL_COLLECTION,
  isEffectiveBoardMember,
  record,
  stringList,
  EMPTY_OVERLAY as PURE_EMPTY_OVERLAY,
  overlayLookupKey,
  FIRESTORE_PERMISSION_DENIED_CODE,
  LISTENER_RETRY_MAX_ATTEMPTS,
  firestoreListenerErrorCode,
  isTransientFirestoreListenerErrorCode,
  listenerRetryDelayMs,
  type BoardMembershipOverlay,
  type MembershipOverlayKey,
} from './boardMembershipOverlay';

/**
 * The keyed answer shape for useBoardMembershipEffectiveMember (merged
 * 2026-09-01, see the function's own docstring below): everything PR #835's
 * OverlayAnswer carries (lookupKey + overlay, so a uid change is stale
 * synchronously during render), plus PR #807's `fromCache` and `error`
 * riding the SAME key so neither can leak from one uid into the next
 * either.
 */
interface KeyedMembershipAnswer {
  /** The lookup this answer is FOR, from overlayLookupKey(). */
  lookupKey: string;
  overlay: BoardMembershipOverlay;
  fromCache: boolean;
  error: boolean;
}

const EMPTY_MEMBERSHIP_ANSWER: KeyedMembershipAnswer = {
  lookupKey: '',
  overlay: PURE_EMPTY_OVERLAY,
  fromCache: false,
  error: false,
};

export {
  EMPTY_OVERLAY,
  isEffectiveBoardMember,
  withAddedMember,
  withClearedOverride,
  withRemovedMember,
  type BoardKey,
  type BoardMembershipOverlay,
  type MembershipOverlayKey,
} from './boardMembershipOverlay';

/**
 * Any active user, per the rules (widened by Codex round 1 on #805,
 * finding 1: the board pages themselves need to read their OWN overlay
 * status to gate correctly - an added-but-not-otherwise-eligible member
 * has no other way to learn they now have the board, and a removed member
 * has no other way to get the honest non-member screen instead of a raw
 * permission error). Same reasoning the derived roll docs already carry:
 * uids are opaque, and knowing the door is open costs nothing to leak.
 * WRITE stays director/manager only - that is the actual security
 * boundary, unaffected by this read widening.
 */
export function watchBoardMembershipOverlay(
  boardKey: MembershipOverlayKey,
  // `fromCache` (Codex P2, PR #807 round 13): with local persistence
  // enabled, a page that mounts with an already-fully-cached copy of this
  // doc gets a snapshot whose DATA already matches the server and never
  // fires again on its own - includeMetadataChanges below is what still
  // hands this callback the later, purely-metadata invocation that flips
  // fromCache from true to false once the server actually confirms it.
  // Every existing caller that ignores the second argument keeps its exact
  // current behaviour; NewMessage.tsx's own announcement-reach gate is the
  // first caller that reads it (see jrStaffBoardReachSettled).
  onData: (overlay: BoardMembershipOverlay, fromCache: boolean) => void,
  onError: (error: Error) => void
): () => void {
  if (!db) {
    onError(new Error('Firestore not initialized.'));
    return () => {};
  }
  return onSnapshot(
    doc(db, OVERLAY_COLLECTION[boardKey], 'current'),
    { includeMetadataChanges: true },
    (snap) => {
      const data = record(snap.data());
      onData(
        { addedUids: stringList(data.addedUids), removedUids: stringList(data.removedUids) },
        snap.metadata.fromCache
      );
    },
    (error) => onError(error instanceof Error ? error : new Error(String(error)))
  );
}

/**
 * The derived auto-roll's family uids, read-only, for the two boards that
 * have one. Any active user may read this doc (shared/firestore.rules), so
 * this is safe on the Manage members sheet even before the overlay itself
 * loads. Staff are members by ROLE, never by this roll - callers combine
 * this with their own staff-role list for a full display roster.
 */
export function watchBoardRollUids(
  boardKey: MembershipOverlayKey,
  // `fromCache` - same reasoning as watchBoardMembershipOverlay's own
  // comment just above (Codex P2, PR #807 round 13): includeMetadataChanges
  // is what lets a cache-only initial snapshot later hand this callback the
  // server-confirmed answer, even when the data itself never changes.
  onData: (memberUids: string[], fromCache: boolean) => void,
  onError: (error: Error) => void
): () => void {
  const collectionName = ROLL_COLLECTION[boardKey];
  if (!collectionName) {
    // No roll doc for this board at all (parentBoard) - nothing was read,
    // so there is nothing to call unconfirmed.
    onData([], false);
    return () => {};
  }
  if (!db) {
    onError(new Error('Firestore not initialized.'));
    return () => {};
  }
  return onSnapshot(
    doc(db, collectionName, 'current'),
    { includeMetadataChanges: true },
    (snap) => onData(stringList(record(snap.data()).memberUids), snap.metadata.fromCache),
    (error) => onError(error instanceof Error ? error : new Error(String(error)))
  );
}

/**
 * ATOMIC PER-UID EDITS (Codex round 1 on #805, finding 3). The original
 * shape here read the overlay into local state, ran it through
 * with*Member, and setDoc'd the WHOLE resulting document back - a classic
 * lost-update race: two managers acting on two different uids within the
 * same snapshot window would each compute their edit from the SAME stale
 * read, and whichever setDoc lands second silently erases the first
 * (including a removal, which means access silently comes back).
 *
 * Firestore's arrayUnion/arrayRemove are FIELD TRANSFORMS, applied
 * server-side against whatever the array currently holds, never against a
 * client's local snapshot - concurrent edits to the two fields (or even
 * the same field) compose correctly with no read-modify-write step at
 * all. setDoc(..., {merge: true}) rather than updateDoc(): the very FIRST
 * edit on a board with no overlay doc yet must CREATE it, which
 * updateDoc() refuses; a merge-set creates or merges either way, and the
 * rules validate the same resulting-document shape regardless of which
 * path got it there.
 */
async function applyOverlayEdit(
  boardKey: MembershipOverlayKey,
  actorUid: string,
  edit: { addedUids: ReturnType<typeof arrayUnion>; removedUids: ReturnType<typeof arrayRemove> }
    | { addedUids: ReturnType<typeof arrayRemove>; removedUids: ReturnType<typeof arrayUnion> }
    | { addedUids: ReturnType<typeof arrayRemove>; removedUids: ReturnType<typeof arrayRemove> }
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await setDoc(doc(db, OVERLAY_COLLECTION[boardKey], 'current'), {
    ...edit,
    updatedAt: serverTimestamp(),
    updatedBy: actorUid,
  }, { merge: true });
}

/** Add uid: grants access, and atomically un-removes it if a stale removal was sitting there. */
export async function addBoardMember(boardKey: MembershipOverlayKey, actorUid: string, uid: string): Promise<void> {
  await applyOverlayEdit(boardKey, actorUid, { addedUids: arrayUnion(uid), removedUids: arrayRemove(uid) });
}

/** Remove uid: takes ACCESS, and atomically drops any stale add for the same uid. */
export async function removeBoardMember(boardKey: MembershipOverlayKey, actorUid: string, uid: string): Promise<void> {
  await applyOverlayEdit(boardKey, actorUid, { addedUids: arrayRemove(uid), removedUids: arrayUnion(uid) });
}

/** Undo either override: back to whatever the derived roll/role says on its own. */
export async function clearBoardMemberOverride(boardKey: MembershipOverlayKey, actorUid: string, uid: string): Promise<void> {
  await applyOverlayEdit(boardKey, actorUid, { addedUids: arrayRemove(uid), removedUids: arrayRemove(uid) });
}

/**
 * ONE HOOK, every board page (Codex round 1 on #805, finding 1). Reads
 * this uid's own overlay entry live and folds it onto whatever the page's
 * own pre-existing predicate (`autoIn`) already decided, through the same
 * isEffectiveBoardMember() the rules mirror. `settled` stays false until
 * the FIRST overlay snapshot (success or error) FOR THIS UID has been seen,
 * so a page does not flash "not a member" for an added-but-not-otherwise-
 * eligible account before the overlay has had a chance to load - the exact
 * pattern the Jr Staff board's own roll-membership wait already uses.
 *
 * FOR THIS UID is load-bearing, and it is why the answer is keyed rather
 * than flagged (Codex review on #835, P2). A caller that starts with no uid
 * and gains one when the user doc resolves - which is every caller, since
 * eligibility is read off that doc - would otherwise carry the earlier
 * phase's settled = true into the first render at the new uid and publish a
 * confident "not a member" about an account nothing had been read for yet.
 * settledOverlayFor() compares the answer's own lookupKey against the one
 * being asked for DURING RENDER, so a uid change invalidates the previous
 * answer synchronously and the caller sees loading, never a false denial.
 * The stale overlay is discarded rather than reused: one uid's membership
 * is not evidence about another's.
 *
 * FAIL DIRECTION on a read error: falls back to the untouched autoIn
 * result (an unreadable overlay is treated the same as an absent one).
 * This is a UI-polish default only, never a security boundary - the
 * ACTUAL read of the board's own posts is independently re-evaluated by
 * the rules' own isCompBoardMember() / isJrStaffBoardMember() /
 * isParentBoardMember(), which read the real overlay doc server-side
 * regardless of what this hook could or could not fetch. Worst case on a
 * transient read error, a removed member briefly sees the page shell
 * before their own thread-list read comes back permission-denied, which
 * every board page's existing error handling already covers.
 *
 * `removed` (added PR #807 round 8, Codex P1-a) exposes the SAME
 * removedUids.includes(uid) check isEffectiveBoardMember already computes
 * internally, so a caller that needs to know "was this uid specifically
 * taken off the roll" (not just "are they currently a member" - autoIn
 * can readmit a removed-then-re-qualifying uid next roll sweep) does not
 * have to duplicate the overlay read. discussionReachesUser in
 * lib/discussionAudience.ts is the first caller: a removed Jr Staff
 * member still carries their staff role, so `member` alone cannot signal
 * removal once autoIn flips back true. `removed` stays honest through
 * that flip because it reads the overlay directly, not the composed
 * result.
 */
export function useBoardMembershipEffectiveMember(
  boardKey: MembershipOverlayKey,
  uid: string | null | undefined,
  autoIn: boolean
): { member: boolean; settled: boolean; removed: boolean; fromCache: boolean; error: boolean } {
  // MERGED 2026-09-01: PR #835 (this same file, on hub-staging) fixed a
  // DIFFERENT bug in this hook - `settled` used to be a bare boolean and
  // lied across a uid change (a caller that starts with no uid, then gains
  // one once the user doc resolves, briefly published a confident "not a
  // member" carried over from the no-uid phase). Its fix keys the answer to
  // the exact (boardKey, uid) lookup it was read FOR, so a uid change makes
  // the previous answer stale synchronously, during render, with no effect
  // in between. That keying is adopted here unchanged. What follows folds
  // PR #807's own `fromCache` (round 13), `removed` (round 8) and `error`
  // (round 15, fail-closed listener errors) onto the SAME keyed shape,
  // rather than the flat overlay/settled state either PR used alone -
  // `fromCache` and `error` need the identical staleness protection
  // `settled` does, or an error/cache flag from a PRIOR uid could leak into
  // a fresh uid's very first render the same way the original bug did.
  const [answer, setAnswer] = useState<KeyedMembershipAnswer>(EMPTY_MEMBERSHIP_ANSWER);
  const lookupKey = overlayLookupKey(boardKey, uid);
  // Which lookupKey has a real, server-or-cache CONFIRMED snapshot behind
  // it right now - not just state, checked inside the onError callback to
  // decide fail-closed-with-error vs preserve-the-last-answer. Naturally
  // scoped per uid with no explicit reset needed: a different uid produces
  // a different lookupKey string, so this can never read as "already
  // confirmed" for a uid it has not actually seen data for yet (proven by
  // tests/board-membership-effective-member-live.test.mjs's "fresh mount
  // after a prior errored mount" case).
  const confirmedForKey = useRef<string | null>(null);
  // CODE-AWARE ERROR HANDLING, NOT A BLANKET PRESERVE (Codex P2a, PR #807
  // round 17). Round 15's preserve-on-error had the same gap jrStaffBoard.
  // ts's sibling roll hook did (see lib/boardMembershipOverlay.ts's own
  // doc comment on firestoreListenerErrorCode for the shared reasoning
  // both hooks now carry): an onSnapshot error is TERMINAL, so "preserve
  // the confirmed answer across an error" quietly meant "preserve it
  // forever" once the listener died, with nothing ever resubscribing it.
  // `retryCountForKey` tracks consecutive transient-retry attempts for the
  // CURRENT lookupKey (an object, not a bare number, so a uid change - a
  // different lookupKey - naturally starts a fresh budget without an
  // explicit reset, the same "keyed rather than flagged" reasoning
  // `confirmedForKey` already uses); `retryTimer` is the pending
  // setTimeout, cleared on every effect rerun and on unmount so a stale
  // timer from an old lookupKey can never fire against a new one.
  const retryCountForKey = useRef<{ key: string; count: number }>({ key: '', count: 0 });
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped to force the effect below to tear down and rebuild its
  // subscription - the SAME unsubscribe/resubscribe shape
  // lib/compBoard.ts's useCompBoardRosterNames `refetch` uses for its own
  // attempt counter, just driven by an automatic scheduled retry here
  // instead of a person's tap (this hook has no visible "Try again" of its
  // own to drive one).
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    if (!uid) {
      // Nothing to read. EMPTY_MEMBERSHIP_ANSWER already answers the empty
      // lookupKey on first mount, but a uid that WAS real and is now gone
      // needs an explicit reset too - otherwise a stale confirmed or
      // errored answer from the PRIOR uid keeps reading as this (no) uid's
      // own settled state.
      setAnswer(EMPTY_MEMBERSHIP_ANSWER);
      return;
    }
    if (retryCountForKey.current.key !== lookupKey) {
      retryCountForKey.current = { key: lookupKey, count: 0 };
    }
    return watchBoardMembershipOverlay(
      boardKey,
      (data, cached) => {
        confirmedForKey.current = lookupKey;
        // ONLY A SERVER-CONFIRMED SNAPSHOT REFILLS THE RETRY BUDGET (Codex
        // P2, PR #807 round 18). Resetting on ANY snapshot, cached
        // included, made the bounded budget unbounded: with local
        // persistence on, each resubscribe can hand this callback a
        // CACHED copy of the overlay straight from disk before the same
        // transient condition kills the listener again, so every failure
        // looked like attempt one, the count never reached
        // LISTENER_RETRY_MAX_ATTEMPTS, and the hook retried forever while
        // every consumer held the same possibly-stale cached answer. The
        // budget exists to stop exactly that, so only the thing a retry
        // is TRYING to get - a fresh answer from the server - is allowed
        // to refill it. A cached emission leaves the count untouched.
        // jrStaffBoard.ts's twin already had this shape by construction
        // (its own onData returns on fromCache BEFORE touching the
        // counter, for the separate round-16 cached-rows-stay-pending
        // reason); the two now match deliberately rather than by
        // accident.
        if (!cached) retryCountForKey.current = { key: lookupKey, count: 0 };
        setAnswer({ lookupKey, overlay: data, fromCache: cached, error: false });
      },
      (err) => {
        const code = firestoreListenerErrorCode(err);
        if (code === FIRESTORE_PERMISSION_DENIED_CODE) {
          // Not a blip, and no resubscribe can clear it. This overlay doc
          // is readable by any ACTIVE user (shared/firestore.rules gates it
          // on isActiveUserByDoc(), NOT on board membership), so a denial
          // says the caller is not an active user at all - signed out,
          // deactivated, retired - which a fresh listener would only meet
          // again. Fails closed at once, overriding even a previously
          // confirmed answer. Full reasoning, and the rule text it was
          // checked against, in lib/boardMembershipOverlay.ts's
          // firestoreListenerErrorCode doc comment.
          confirmedForKey.current = null;
          setAnswer({ lookupKey, overlay: PURE_EMPTY_OVERLAY, fromCache: false, error: true });
          return;
        }
        const retryState = retryCountForKey.current.key === lookupKey
          ? retryCountForKey.current
          : { key: lookupKey, count: 0 };
        if (isTransientFirestoreListenerErrorCode(code) && retryState.count < LISTENER_RETRY_MAX_ATTEMPTS) {
          retryState.count += 1;
          retryCountForKey.current = retryState;
          const delay = listenerRetryDelayMs(retryState.count);
          retryTimer.current = setTimeout(() => setAttempt((n) => n + 1), delay);
          // Leave `answer` exactly as it is while the retry works in the
          // background - PRESERVE the last real snapshot for THIS uid if
          // there is one (overlay/fromCache already hold it, untouched
          // here), or stay unsettled if there is not. Real, possibly-stale
          // data beats a scary flip to error for a blip that is about to
          // self-heal.
          return;
        }
        // Retries exhausted, or a code this hook does not recognize as
        // transient - fail closed, overriding whatever was being
        // preserved. An indefinitely retried, indefinitely stale confirmed
        // answer is its own kind of wrong, not meaningfully safer than
        // admitting the read failed. `fromCache`/`settled` alone cannot
        // carry that signal: both already read as "resolved" the instant
        // this error callback runs.
        confirmedForKey.current = null;
        setAnswer({ lookupKey, overlay: PURE_EMPTY_OVERLAY, fromCache: false, error: true });
      }
    );
  }, [boardKey, uid, lookupKey, attempt]);

  // Cleanup on unmount only - the effect's own cleanup above already
  // clears a pending timer on every rerun; this catches the LAST one, when
  // there is no further rerun to do it.
  useEffect(() => () => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
  }, []);

  // Compared during RENDER, so a uid change is stale immediately rather
  // than one effect later - same reasoning as PR #835's settledOverlayFor,
  // applied to the whole answer (overlay AND fromCache AND error) instead
  // of just the overlay.
  const settledAnswer = answer.lookupKey === lookupKey ? answer : null;
  const overlay = settledAnswer?.overlay ?? PURE_EMPTY_OVERLAY;
  // `member` keeps EXACTLY the semantics it always had: while no answer for
  // this uid has landed, the overlay reads as absent and the result is the
  // caller's own untouched autoIn. That matters because several call sites
  // (NavDrawer's board rows, Messages' comp-board section, FamilyCommand
  // Center's board doors) consume `member` WITHOUT consulting `settled`,
  // and folding the wait into `member` would silently change what they
  // draw on a first paint.
  const member = Boolean(uid) && isEffectiveBoardMember({ uid: uid ?? '', autoIn, overlay });
  // `removed` (PR #807 round 8, Codex P1-a): the SAME removedUids check
  // isEffectiveBoardMember already computes internally, exposed for a
  // caller that needs "was this uid specifically taken off the roll", not
  // just "are they currently a member" (see the docstring above).
  const removed = Boolean(uid) && overlay.removedUids.includes(uid ?? '');
  return {
    member,
    settled: settledAnswer !== null,
    removed,
    fromCache: settledAnswer?.fromCache ?? false,
    error: settledAnswer?.error ?? false,
  };
}
